import type { RootStore } from './RootStore';

interface EmailVerifiedPayload {
  email: string;
  verifiedAt: string;
}

interface EmailVerificationFailedPayload {
  email: string;
  error: string;
  verificationId: string;
}

interface EmailVerificationExpiredPayload {
  email: string;
  expiredAt: string;
}

interface ExternalValidationSubmittedPayload {
  email: string;
  entityName: string;
  recordId?: string;
  changedFields: string[];
  verified?: boolean;
  submittedAt: string;
}

/**
 * Register SSE handlers for email verification events.
 * Called from RootStore constructor.
 */
export function registerEmailVerificationSSEHandlers(rootStore: RootStore): void {
  const { eventSourceStore, notificationStore } = rootStore;

  eventSourceStore.registerHandler(
    'emailVerification.verified',
    (payload: EmailVerifiedPayload) => {
      notificationStore.addNotification({
        type: 'success',
        title: 'Email verificado',
        message: `${payload.email} ha sido verificado.`,
        data: { type: 'emailVerification.verified' },
      });
      window.dispatchEvent(new CustomEvent('emailVerificationChanged', { detail: payload }));
    }
  );

  eventSourceStore.registerHandler(
    'emailVerification.failed',
    (payload: EmailVerificationFailedPayload) => {
      // El detalle técnico (payload.error) va a consola para diagnóstico; al
      // usuario le mostramos un mensaje claro y accionable, no el error crudo.
      // El envío es reintentable, por eso 'warning' y no 'error'.
      if (payload.error) {
        console.warn(`[emailVerification.failed] ${payload.email}:`, payload.error);
      }
      notificationStore.addNotification({
        type: 'warning',
        title: 'No se pudo enviar la verificación',
        message: `No pudimos enviar el email de verificación a ${payload.email}. Verificá que la dirección sea correcta y volvé a intentar.`,
        data: { type: 'emailVerification.failed', verificationId: payload.verificationId },
      });
      window.dispatchEvent(new CustomEvent('emailVerificationChanged', { detail: payload }));
    }
  );

  eventSourceStore.registerHandler(
    'emailVerification.expired',
    (payload: EmailVerificationExpiredPayload) => {
      notificationStore.addNotification({
        type: 'warning',
        title: 'Verificación expirada',
        message: `La verificación de ${payload.email} ha expirado.`,
        data: { type: 'emailVerification.expired' },
      });
      window.dispatchEvent(new CustomEvent('emailVerificationChanged', { detail: payload }));
    }
  );

  eventSourceStore.registerHandler(
    'externalValidation.submitted',
    (payload: ExternalValidationSubmittedPayload) => {
      // El aviso de la campana lo guarda el server para cada administrador de
      // sitio y llega por 'notification': acá no se arma uno local (el de antes
      // le llegaba a quien estuviera conectado y no quedaba guardado). Los
      // formularios abiertos se enteran para releer el registro (recordId).
      window.dispatchEvent(new CustomEvent('emailVerificationChanged', { detail: payload }));
      window.dispatchEvent(new CustomEvent('externalValidationSubmitted', { detail: payload }));
    }
  );
}
